
import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MapPin, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ISRAEL_CITIES, LocationOption } from '@/data/israelCities';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onLocationSelect: (location: { name: string; lat: string; lng: string; locationId: number }) => void;
  placeholder?: string;
  className?: string;
}

const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  onLocationSelect,
  placeholder = "הזן שם עיר",
  className
}) => {
  const [suggestions, setSuggestions] = useState<LocationOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter cities based on input
  useEffect(() => {
    if (value.length >= 1) {
      const filtered = ISRAEL_CITIES.filter(city =>
        city.name.includes(value) || value.includes(city.name)
      ).slice(0, 8);
      setSuggestions(filtered);
      setShowDropdown(filtered.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [value]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleLocationSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle location selection
  const handleLocationSelect = (location: LocationOption) => {
    onChange(location.name);
    onLocationSelect({
      name: location.name,
      lat: location.lat.toString(),
      lng: location.lng.toString(),
      locationId: location.id
    });
    setShowDropdown(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    
    const index = text.indexOf(query);
    if (index === -1) return text;
    
    return (
      <>
        {text.substring(0, index)}
        <strong className="font-semibold text-blue-600">
          {text.substring(index, index + query.length)}
        </strong>
        {text.substring(index + query.length)}
      </>
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => value.length >= 1 && suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="pr-10 text-right"
          dir="rtl"
        />
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((city, index) => (
            <button
              key={city.id}
              type="button"
              onClick={() => handleLocationSelect(city)}
              className={cn(
                "w-full px-4 py-3 text-right hover:bg-gray-50 transition-colors duration-150",
                "border-b border-gray-100 last:border-b-0",
                "focus:outline-none focus:bg-gray-50",
                selectedIndex === index && "bg-blue-50 hover:bg-blue-50"
              )}
              dir="rtl"
            >
              <div className="flex items-center justify-between">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="text-sm text-right flex-1 mr-2">
                  <div className="font-medium text-gray-900">
                    {highlightMatch(city.name, value)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    ID: {city.id}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
